import { forwardRef } from "react";

import { formatTimeWithTimezone } from "@simplyk/common";
import { DateTime } from "luxon";

import { useLocaleContext } from "@/contexts/LocaleContext";
import { BaseFormDate, BaseFormDateProps } from "@/forms-design-system/FormDate/BaseFormDate";
import { getDate, isMultiDayDate, shouldDisplayYear } from "@/forms-design-system/FormDate/helper";
import { useTranslate } from "@/hooks/useTranslate";

export type SimpleFormDateRangeProps = Omit<BaseFormDateProps, "date" | "title" | "subtitle"> & {
  startDate: Date;
  endDate: Date;
  now?: Date;
  timezone?: string | null;
};

export const SimpleFormDateRange = forwardRef<HTMLDivElement, SimpleFormDateRangeProps>(
  ({ startDate, endDate, now, timezone, ...rest }, ref) => {
    const { t } = useTranslate();
    const { isoLocale } = useLocaleContext();
    const luxonNow = now ? getDate(now) : DateTime.now();
    const luxonStartDate = getDate(startDate);
    const luxonEndDate = getDate(endDate);
    const multiDay = isMultiDayDate(luxonStartDate, luxonEndDate);

    const startTimeDisplay = luxonStartDate.toLocaleString(
      { hour: "numeric", minute: "2-digit" },
      { locale: isoLocale }
    );

    const endTimeDisplay = timezone
      ? formatTimeWithTimezone(endDate, isoLocale, timezone)
      : luxonEndDate.toLocaleString({ hour: "numeric", minute: "2-digit" }, { locale: isoLocale });

    if (multiDay) {
      return (
        <BaseFormDate
          date={startDate}
          title={t("common", "dateRangeStartDate", {
            date: luxonStartDate.toLocaleString(
              {
                year: shouldDisplayYear(luxonStartDate, luxonNow) ? "numeric" : undefined,
                month: "short",
                day: "numeric",
              },
              { locale: isoLocale }
            ),
            time: startTimeDisplay,
          })}
          subtitle={t("common", "dateRangeEndDate", {
            date: luxonEndDate.toLocaleString(
              {
                year: shouldDisplayYear(luxonEndDate, luxonNow) ? "numeric" : undefined,
                month: "short",
                day: "numeric",
              },
              { locale: isoLocale }
            ),
            time: endTimeDisplay,
          })}
          {...rest}
          ref={ref}
        />
      );
    }

    return (
      <BaseFormDate
        date={startDate}
        title={luxonStartDate.toLocaleString(
          {
            month: "short",
            day: "numeric",
            year: shouldDisplayYear(luxonStartDate, luxonNow) ? "numeric" : undefined,
          },
          { locale: isoLocale }
        )}
        subtitle={t("common", "timeRange", {
          startTime: startTimeDisplay,
          endTime: endTimeDisplay,
        })}
        {...rest}
        ref={ref}
      />
    );
  }
);

SimpleFormDateRange.displayName = "SimpleFormDateRange";
